import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Vérifier si le consentement a déjà été donné
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      setShowBanner(true);
    } else {
      // Charger les préférences sauvegardées
      const savedPreferences = localStorage.getItem('cookiePreferences');
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setPreferences(onlyNecessary);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', 'true');
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Les cookies nécessaires ne peuvent pas être désactivés
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) {
    // Bouton pour rouvrir les paramètres
    return (
      <button
        onClick={() => setShowSettings(true)}
        className="fixed bottom-6 right-6 bg-gray-800 border border-gray-700 rounded-lg p-3 hover:bg-gray-700 transition-colors shadow-lg z-40 group"
        aria-label="Gérer les cookies"
      >
        <Cookie className="text-pink-400 group-hover:text-pink-300" size={24} />
      </button>
    );
  }

  return (
    <>
      {showSettings ? (
        // Panneau de paramètres détaillés
        <div className="fixed bottom-6 right-6 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-40 w-96 max-w-[calc(100vw-3rem)]">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center space-x-2">
                <Cookie className="text-pink-400" size={24} />
                <span>Paramètres des Cookies</span>
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-6">
              Personnalisez vos préférences de cookies. Les cookies nécessaires ne peuvent pas être désactivés.
            </p>

            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-white font-semibold">Cookies Nécessaires</h4>
                    <span className="text-xs bg-pink-600 text-white px-2 py-0.5 rounded">Obligatoire</span>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Essentiels pour le fonctionnement du site
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-10 h-6 bg-pink-600 rounded-full flex items-center justify-end px-1">
                    <Check className="text-white" size={14} />
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">Cookies d'Analyse</h4>
                  <p className="text-gray-400 text-xs">
                    Nous aident à comprendre comment vous utilisez le site
                  </p>
                </div>
                <button
                  onClick={() => togglePreference('analytics')}
                  className={`ml-4 w-10 h-6 rounded-full transition-colors flex items-center ${
                    preferences.analytics ? 'bg-pink-600 justify-end' : 'bg-gray-600 justify-start'
                  } px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="text-white font-semibold mb-1">Cookies Marketing</h4>
                  <p className="text-gray-400 text-xs">
                    Utilisés pour personnaliser les publicités
                  </p>
                </div>
                <button
                  onClick={() => togglePreference('marketing')}
                  className={`ml-4 w-10 h-6 rounded-full transition-colors flex items-center ${
                    preferences.marketing ? 'bg-pink-600 justify-end' : 'bg-gray-600 justify-start'
                  } px-1`}
                >
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleSavePreferences}
                className="w-full px-4 py-2 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg font-semibold hover:from-pink-700 hover:to-pink-800 transition-all"
              >
                Enregistrer les Préférences
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors text-sm"
                >
                  Tout Accepter
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors text-sm"
                >
                  Tout Refuser
                </button>
              </div>
            </div>

            <p className="text-gray-500 text-xs text-center mt-4">
              <a href="/cookies" data-discover="true" className="text-pink-400 hover:text-pink-300 underline">
                En savoir plus sur notre politique des cookies
              </a>
            </p>
          </div>
        </div>
      ) : (
        // Bannière simple en bas à droite
        <div className="fixed bottom-6 right-6 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl z-40 w-96 max-w-[calc(100vw-3rem)]">
          <div className="p-5">
            <div className="flex items-start space-x-3 mb-4">
              <Cookie className="text-pink-400 flex-shrink-0 mt-1" size={24} />
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Nous utilisons des cookies</h3>
                <p className="text-gray-400 text-sm">
                  Nous utilisons des cookies pour améliorer votre expérience et analyser l'utilisation du site.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleAcceptAll}
                className="w-full px-4 py-2 bg-gradient-to-r from-pink-600 to-pink-700 text-white rounded-lg font-semibold hover:from-pink-700 hover:to-pink-800 transition-all text-sm"
              >
                Tout Accepter
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowSettings(true)}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors text-sm flex items-center justify-center space-x-1"
                >
                  <Settings size={14} />
                  <span>Personnaliser</span>
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors text-sm"
                >
                  Refuser
                </button>
              </div>
            </div>

            <p className="text-gray-500 text-xs text-center mt-4">
              <a href="/cookies" data-discover="true" className="text-pink-400 hover:text-pink-300 underline">
                Politique des cookies
              </a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;

